import { Col, Pagination, PaginationProps, Row, Table, TableProps, Typography } from "antd";
import moment from "moment";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  FILTER_VALUE_EACH_TABS,
  ResponseGetDistinctSettingHistory,
  ResponseSettingHistory,
  SETTING_HISTORY_TAB,
  SettingHistoryFilters,
} from "src/entity/settings/setting-history";
import { useDidMount } from "src/hook/useDidMount";
import { usePagination } from "src/hook/usePagination";
import { profileStore } from "src/stores/profile-store";
import { settingHistoryStore } from "src/stores/setting-history-store";
import { componentPalette } from "src/theme/component-palette";
import { capitalizeFirstLetter } from "src/utils/manage-string";
import { useShallow } from "zustand/react/shallow";

const { Text } = Typography;

const titleBar = [
  {
    parent: "master_data",
    key: SETTING_HISTORY_TAB.MASTER_DATA,
    value: JSON.parse(FILTER_VALUE_EACH_TABS.MASTER_DATA),
    label: "Master Data",
    type: "main",
    colSpan: "6",
  },
  {
    parent: "company",
    key: SETTING_HISTORY_TAB.COMPANY,
    value: JSON.parse(FILTER_VALUE_EACH_TABS.COMPANY),
    label: "Company",
    type: "main",
    colSpan: "5",
  },

  {
    parent: "user_management",
    key: SETTING_HISTORY_TAB.USER_MANAGEMENT,
    value: JSON.parse(FILTER_VALUE_EACH_TABS.USER_MANAGEMENT),
    label: "User Management",
    type: "main",
    colSpan: "8",
  },
  {
    parent: "job",
    key: SETTING_HISTORY_TAB.JOB,
    value: JSON.parse(FILTER_VALUE_EACH_TABS.JOB),
    label: "Job",
    type: "main",
    colSpan: "5",
  },
];

const SettingHistory: React.FC = () => {
  const [activeTab, setActiveTab] = useState<SETTING_HISTORY_TAB>(SETTING_HISTORY_TAB.MASTER_DATA);
  const [triggerLoad, setTriggerLoad] = useState<number>(0);
  const [loadingPromise, setLoadingPromise] = useState<boolean>(false);

  const profileCompany = profileStore(useShallow((state) => state.profile?.company));
  // console.log("Profile ocmpany : ", profileCompany);
  const getDistinctSettingHistoryColumns = settingHistoryStore(useShallow((state) => state.getDistinctSettingHistoryColumns));
  const dataDistinct = settingHistoryStore(useShallow((state) => state.dataDistinct));

  //   console.log(dataDistinct);

  const getSettingHistory = settingHistoryStore(useShallow((state) => state.getSettingHistory));
  const dataSettingHistory = settingHistoryStore(useShallow((state) => state.data));
  const totalDocs = settingHistoryStore(useShallow((state) => state.total));

  const {
    onChange,
    filter,
    page,
    setPage,
    loading,
    limit: pageSize,
    setLimit: setPageSize,
    data: sourceData,
  } = usePagination<ResponseSettingHistory, SettingHistoryFilters>({
    action: getSettingHistory,
    remoteData: dataSettingHistory,
    initialFilter: { category: titleBar[0].value },
  });

  const onChangePage = (val: number) => {
    setPage(val);
  };
  const handleTableChange: TableProps<ResponseSettingHistory>["onChange"] = (_, filters) => {
    onChange(filters);
    setTriggerLoad(triggerLoad + 1);
  };
  const handleChangeTab = (tab: SETTING_HISTORY_TAB) => {
    const findSlot = titleBar.find((e) => e.key === tab);
    onChange({ ...filter, category: findSlot?.value });
    getDistinctSettingHistoryColumns({ tab });
    setActiveTab(tab);
  };

  const paginationProps: PaginationProps = {
    size: "small",
    pageSize: pageSize,
    current: page,
    total: totalDocs || 0,
    defaultCurrent: 1,
    onChange: onChangePage,
    showSizeChanger: false,
  };

  const renderDetails = useCallback(
    (record: ResponseSettingHistory) => {
      switch (activeTab) {
        case SETTING_HISTORY_TAB.MASTER_DATA: {
          const menuName = record.menu.slice(7).replaceAll("_", " ");
          const tabName = record.tab.replaceAll("_", " ");
          return capitalizeFirstLetter(menuName) + ` (${capitalizeFirstLetter(tabName)})`;
        }
        case SETTING_HISTORY_TAB.JOB: {
          const tabName = record.tab.replaceAll("_", " ");
          const recentValue = (record?.recentValue as { yearFrom?: string; yearTo?: string; companyId?: string }) ?? {};
          const companyId = recentValue?.companyId || "";
          const companyName = profileCompany?.find((company) => company?._id.toString() === companyId.toString())?.companyName || "All";

          if (tabName.toLocaleLowerCase().replaceAll(" ", "") === "duplicatedata") {
            const yearFrom = recentValue?.yearFrom || "";
            const yearTo = recentValue?.yearTo || "";
            return `${tabName} from ${yearFrom} to ${yearTo} of ${companyName}`;
          } else if (tabName.toLocaleLowerCase().replaceAll(" ", "") === "fugitivedataimportfromdatafactory") {
            return `${tabName} (${companyName})`;
          } else {
            return `${tabName}`;
          }
        }
        case SETTING_HISTORY_TAB.USER_MANAGEMENT: {
          const recentValue = record.recentValue as { email: string };
          const tabName = recentValue?.email;
          return `${tabName}`;
        }
        case SETTING_HISTORY_TAB.COMPANY: {
          const recentValue = record.recentValue as { company: string };
          const lastValue = record.lastValue as { company: { companyName: string } };
          // console.log("recentValue :: ", recentValue);
          const findCompany = profileCompany ? profileCompany?.find((item) => item._id === recentValue?.company) : null;
          const finalDetail = findCompany ? findCompany.companyName : lastValue?.company?.companyName;
          return `${finalDetail ? finalDetail : ""}`;
        }
        default:
          return record.menu;
      }
    },
    [sourceData, activeTab, profileCompany],
  );

  const getIndex = (index: number) => (page - 1) * pageSize + index;
  const getColumns = (distinctColumns: ResponseGetDistinctSettingHistory | null) => {
    const filterActions = distinctColumns?.action ?? [];
    const columns: TableProps<ResponseSettingHistory>["columns"] = [
      {
        title: "No",
        key: "no",
        render: (_, __, index) => getIndex(index + 1),
        align: "center",
      },
      {
        title: "Action Datetime",
        key: "createdAt",
        render: (_, record) => <Text>{moment(record.updatedAt || record.createdAt).format("YYYY-MM-DD HH:mm:ss")}</Text>,
      },
      {
        title: "Action By",
        dataIndex: "createdBy",
        key: "createdBy",
        filters: distinctColumns?.createdBy ?? [],
        render: (_, record) => <Text>{record.createdBy}</Text>,
      },
      {
        title: "Action",
        key: "action",
        filters: filterActions.map((item) => ({ ...item, text: capitalizeFirstLetter(item.text) })),
        render: (_, record) => <Text>{capitalizeFirstLetter(record.action)}</Text>,
      },
      {
        title: "Detail",
        key: "detail",
        render: (_, record) => <Text>{renderDetails(record)}</Text>,
      },
    ];

    return columns;
  };

  const initialGetSettingJob = async (additionalFilters?: SettingHistoryFilters) => {
    setLoadingPromise(true);
    const getSettingHistoryPromise = getSettingHistory({ ...filter, page, limit: pageSize, ...additionalFilters });
    const getDistinct = getDistinctSettingHistoryColumns({ tab: activeTab });

    await Promise.all([getSettingHistoryPromise, getDistinct]);
    setLoadingPromise(false);
  };

  useDidMount(() => {
    initialGetSettingJob();
  });

  useEffect(() => {
    if (triggerLoad > 0) initialGetSettingJob();
  }, [triggerLoad]);

  return (
    <div>
      <Row gutter={0} style={{ display: "flex", marginBottom: 16, width: "100%" }}>
        {titleBar.map((tab, index) => {
          let borderRadius = "";
          if (["master_data"].includes(tab.key)) {
            borderRadius = "50px 0 0 50px";
          } else if (["job"].includes(tab.key)) {
            borderRadius = "0px 50px 50px 0px";
          } else {
            borderRadius = "0";
          }
          return (
            <Col span={tab.colSpan} key={tab.key + index}>
              <div
                key={tab.key}
                onClick={() => handleChangeTab(tab.key)}
                style={{
                  cursor: "pointer",
                  padding: "8px 16px",
                  borderRadius: borderRadius,
                  backgroundColor: activeTab === tab.key ? componentPalette.grey : "rgba(0, 0, 0, 0.02)",
                  textAlign: "center",
                  paddingLeft: "50px",
                  border: "1px solid #d9d9d9",
                }}
              >
                {tab.label}
              </div>
            </Col>
          );
        })}
      </Row>

      <Row>
        <Table<ResponseSettingHistory>
          columns={getColumns(dataDistinct)}
          dataSource={sourceData as ResponseSettingHistory[]}
          className="w-full"
          loading={loadingPromise || loading}
          pagination={false}
          onChange={handleTableChange}
          rowKey={(record) => `${record._id}`}
        />

        <Row className="w-full mt-4" justify={"end"}>
          <Pagination {...paginationProps} />
        </Row>
      </Row>
    </div>
  );
};

export default SettingHistory;
